import type { LocaleCatalog, MessageSchema } from '../locale';

/**
 * ============================================================================
 * @altkey/i18n - English Catalog (en)
 * ============================================================================
 * Fully implements `MessageSchema` defined in `../locale.ts`. Any missing or
 * extra key compared to the schema causes a TypeScript compilation error:
 * this guarantees the English catalog stays 100% in sync with the other 4
 * supported languages.
 * ============================================================================
 */

const messages: MessageSchema = {
  common: {
    appName: 'AltKey',
    ok: 'OK',
    cancel: 'Cancel',
    confirm: 'Confirm',
    loading: 'Loading…',
    retry: 'Retry',
    close: 'Close',
    copy: 'Copy',
    copied: 'Copied to clipboard',
  },
  pairing: {
    title: 'Pair your devices',
    scanQrPrompt: 'Scan the QR code with your smartphone to pair it with this computer',
    enterCodeManually: 'Or enter the 6-digit code manually',
    waitingForDesktop: 'Waiting for the computer to connect…',
    waitingForMobile: 'Waiting for the smartphone to connect…',
    connected: 'Device connected',
    disconnected: 'Device disconnected',
    expired: 'The pairing session has expired. Generate a new code.',
    invalidCode: 'Invalid code. Please check and try again.',
    keysExchanged: 'Security key exchange completed',
    scanDevice: 'Scan a device',
    showMyCode: 'Show my code',
    hostingPrompt: 'Scan this code with the other phone',
    goBack: 'Torna Indietro',
    disconnectAction: 'Scollega',
    deviceConnected: '🟢 Dispositivo Connesso',
    backToCategories: '← Torna alle Categorie',
  },
  license: {
    proUnlockedPromo: 'AltKey is in worldwide free Beta: all Pro features are unlocked',
    proUnlockedLicense: 'Pro features unlocked with your license',
    freeTierPrompt: 'Unlock Pro features with an AltKey license',
    invalidLicense: 'The entered licenseKey is not valid',
    enterLicenseKey: 'Enter your license key',
  },
  search: {
    placeholder: 'Search for a symbol, character, or keyword…',
    noResults: 'No results found',
    resultsCount: '{count} results found',
    filterByCategory: 'Filter by category',
    recentSymbols: 'Recently used symbols',
  },
  tofuDetector: {
    title: 'Tofu Detector',
    scanning: 'Scanning for missing characters…',
    issuesFound: 'Found {count} non-renderable characters (tofu)',
    noIssuesFound: 'No missing characters detected: all good',
    replaceSuggestion: 'Replace with a compatible alternative character',
  },
  macroGenerator: {
    title: 'Macro Generator',
    recordMacro: 'Record new macro',
    stopRecording: 'Stop recording',
    saveMacro: 'Save macro',
    macroSaved: 'Macro saved successfully',
    deleteMacro: 'Delete macro',
  },
  aiCanvasInput: {
    title: 'AI Canvas Input',
    drawPrompt: 'Draw the symbol you are looking for with your finger or stylus',
    recognizing: 'Recognizing your drawing…',
    recognitionFailed: 'I could not recognize the symbol. Try drawing it again.',
    insertSymbol: 'Insert symbol',
  },
  settings: {
    title: 'Settings',
    language: 'Language',
    theme: 'Theme',
    themeLight: 'Light',
    themeDark: 'Dark',
    themeSystem: 'Follow system',
    startOnBoot: 'Launch when the computer starts',
    notifications: 'Notifications',
    about: 'About',
    version: 'Version {version}',
    checkForUpdates: 'Check for updates',
    signOut: 'Sign out',
  },
  home: {
    title: 'Home',
    greeting: 'Hi, {name}',
    pairWithDesktop: 'Pair with computer',
    recentActivity: 'Recent activity',
    quickActions: 'Quick actions',
    noRecentActivity: 'No recent activity to show',
  },
  errors: {
    generic: 'An unexpected error occurred. Please try again.',
    network: 'Connection error. Check your network and try again.',
    sessionExpired: 'Your session has expired. Please sign in again.',
    unauthorized: 'You are not authorized to perform this action.',
    notFound: 'Resource not found.',
  },
};

const catalog: LocaleCatalog = {
  locale: 'en',
  messages,
};

export default catalog;